import { Headers } from 'pouchdb-fetch';
import { clone } from 'pouchdb-utils';

function getConfigUrl(nodeName) {
  return (nodeName ? '/_node/' + nodeName  : '') + '/_config';
}

function wrapError(err) {
  if (err.name === 'unknown_error') {
    err.message = (err.message || '') +
        ' Unknown error!  Did you remember to enable CORS?';
  }
  throw err;
}

// Wrapper around the fetch API to send and receive JSON objects
// Similar to fetchJSON in pouchdb-adapter-http, but both functions are private.
// Consider extracting them to a common library.
function fetchJSON(dbFetch, path, options) {
  options = options || {};

  if (options.body) {
    options.body = JSON.stringify(options.body);
  }

  options.headers = options.headers || new Headers();
  options.headers.set('Content-Type', 'application/json');
  options.headers.set('Accept', 'application/json');

  return dbFetch(path, options).then(function (response) {
    if (response.ok) {
      return response.json();
    } else {
      return response.json().then(function (responseError) {
        throw responseError;
      });
    }
  }).catch(wrapError);
}

// toCallback takes a function that returns a promise,
// and returns a function that can be used with a callback as the last argument,
// but still retains the ability to be used without a callback and return a promise.
// (Roughly speaking, the opposite of `toPromise` from pouchdb-utils,
// but more suited for `fetch`, that returns a promise by default.)
function toCallback(func) {
  return function () {
    var args = [], len = arguments.length;
    while ( len-- ) args[ len ] = arguments[ len ];

    if (typeof args[-1] === 'function') {
      var callback = args[-1];
      return func.apply(this, args.slice(0, -2)).then(
        function (res) { return callback(null, res); },
        function (err) { return callback(err); }
      );
    } else {
      return func.apply(this, args);
    }
  };
}

var AuthError = /*@__PURE__*/(function (Error) {
  function AuthError(message) {
    Error.call(this, message);
    this.name = 'authentication_error';
    this.status = 400;
    this.error = true;

    // This is needed because extending built-in classes like Error
    // can have issues with the prototype chain in TypeScript/JavaScript
    Object.setPrototypeOf(this, AuthError.prototype);
  }

  if ( Error ) AuthError.__proto__ = Error;
  AuthError.prototype = Object.create( Error && Error.prototype );
  AuthError.prototype.constructor = AuthError;

  return AuthError;
}(Error));

var getMembership = toCallback(function (opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }

  var path = '/_membership';
  var ajaxOpts = Object.assign({
    method: 'GET',
  }, opts.ajax || {});
  return fetchJSON(db.fetch, path, ajaxOpts);
});

var getNodeName = function (db, opts) {
  return db.getMembership(opts).then(
    function (membership) {
      // Some couchdb-2.x-like server
      return membership.all_nodes[0];
    },
    function (error) {
      if (error.error !== 'illegal_database_name') {
        throw error;
      } else {
        // Some couchdb-1.x-like server
        return undefined;
      }
    }
  );
};

var signUpAdmin = toCallback(function (username, password, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('This plugin only works for the http/https adapter. ' +
      'So you should use new PouchDB("http://mysite.com:5984/mydb") instead.'));
  } else if (!username) {
    return Promise.reject(new AuthError('You must provide a username'));
  } else if (!password) {
    return Promise.reject(new AuthError('You must provide a password'));
  }

  return getNodeName(db, opts).then(function (nodeName) {
    var configUrl = getConfigUrl(nodeName);
    var url = (opts.configUrl || configUrl) + '/admins/' + encodeURIComponent(username);
    var ajaxOpts = Object.assign({
      method: 'PUT',
      body: password
    }, opts.ajax || {});
    return fetchJSON(db.fetch, url, ajaxOpts);
  });
});

var deleteAdmin = toCallback(function (username, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('This plugin only works for the http/https adapter. ' +
      'So you should use new PouchDB("http://mysite.com:5984/mydb") instead.'));
  } else if (!username) {
    return Promise.reject(new AuthError('You must provide a username'));
  }

  return getNodeName(db, opts).then(function (nodeName) {
    var configUrl = getConfigUrl(nodeName);
    var url = (opts.configUrl || configUrl) + '/admins/' + encodeURIComponent(username);
    var ajaxOpts = Object.assign({
      method: 'DELETE',
      processData: false
    }, opts.ajax || {});
    return fetchJSON(db.fetch, url, ajaxOpts);
  });
});

var sessionPath = '/_session';

var logIn = toCallback(function (username, password, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('this plugin only works for the http/https adapter'));
  }

  if (!username) {
    return Promise.reject(new AuthError('you must provide a username'));
  } else if (!password) {
    return Promise.reject(new AuthError('you must provide a password'));
  }

  var path = sessionPath;
  var ajaxOpts = Object.assign({
    method: 'POST',
    body: {name: username, password: password},
  }, opts.ajax || {});
  return fetchJSON(db.fetch, path, ajaxOpts);
});

var logOut = toCallback(function (opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }

  var path = sessionPath;
  var ajaxOpts = Object.assign({
    method: 'DELETE'
  }, opts.ajax || {});
  return fetchJSON(db.fetch, path, ajaxOpts);
});

var getSession = toCallback(function (opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }

  var path = sessionPath;
  var ajaxOpts = Object.assign({
    method: 'GET'
  }, opts.ajax || {});
  return fetchJSON(db.fetch, path, ajaxOpts);
});

var usersPath = '/_users';

function updateUser(db, user, opts) {
  var reservedWords = [
    '_id',
    '_rev',
    'name',
    'type',
    'roles',
    'password',
    'password_scheme',
    'iterations',
    'derived_key',
    'salt' ];

  if (opts.metadata) {
    for (var key in opts.metadata) {
      if (opts.metadata.hasOwnProperty(key) && reservedWords.indexOf(key) !== -1) {
        return Promise.reject(new AuthError('cannot use reserved word in metadata: "' + key + '"'));
      }
    }
    user = Object.assign(user, opts.metadata);
  }

  if (opts.roles) {
    user = Object.assign(user, {roles: opts.roles});
  }
  var path = usersPath + '/' + encodeURIComponent(user._id);
  var ajaxOpts = Object.assign({
    method: 'PUT',
    body: user
  }, opts.ajax || {});

  return fetchJSON(db.fetch, path, ajaxOpts);
}

var signUp = toCallback(function (username, password, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('This plugin only works for the http/https adapter. ' +
      'So you should use new PouchDB("http://mysite.com:5984/mydb") instead.'));
  } else if (!username) {
    return Promise.reject(new AuthError('You must provide a username'));
  } else if (!password) {
    return Promise.reject(new AuthError('You must provide a password'));
  }

  var userId = 'org.couchdb.user:' + username;
  var user = {
    name: username,
    password: password,
    roles: [],
    type: 'user',
    _id: userId,
  };

  return updateUser(db, user, opts);
});

var getUser = toCallback(function (username, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (!username) {
    return Promise.reject(new AuthError('you must provide a username'));
  }

  var path = usersPath + '/' + encodeURIComponent('org.couchdb.user:' + username);
  var ajaxOpts = Object.assign({
    method: 'GET'
  }, opts.ajax || {});
  return fetchJSON(db.fetch, path, ajaxOpts).then(function (x) { console.log(x); return x;});
});

var putUser = toCallback(function (username, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('This plugin only works for the http/https adapter. ' +
      'So you should use new PouchDB("http://mysite.com:5984/mydb") instead.'));
  } else if (!username) {
    return Promise.reject(new AuthError('You must provide a username'));
  }

  return db.getUser(username, opts).then(function (user) { return updateUser(db, user, opts); });
});

var deleteUser = toCallback(function (username, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('This plugin only works for the http/https adapter. ' +
      'So you should use new PouchDB("http://mysite.com:5984/mydb") instead.'));
  } else if (!username) {
    return Promise.reject(new AuthError('You must provide a username'));
  }

  return db.getUser(username, opts).then(function (user) {
    var path = usersPath + '/' + encodeURIComponent(user._id) + '?rev=' + user._rev;
    var ajaxOpts = Object.assign({
      method: 'DELETE'
    }, opts.ajax || {});
    return fetchJSON(db.fetch, path, ajaxOpts);
  });
});

var changePassword = toCallback(function (username, password, opts) {
  var db = this;
  if (typeof opts === 'undefined') {
    opts = {};
  }
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('This plugin only works for the http/https adapter. ' +
      'So you should use new PouchDB("http://mysite.com:5984/mydb") instead.'));
  } else if (!username) {
    return Promise.reject(new AuthError('You must provide a username'));
  } else if (!password) {
    return Promise.reject(new AuthError('You must provide a password'));
  }
  return db.getUser(username, opts).then(function (user) {
    user.password = password;
    var path = usersPath + '/' + encodeURIComponent(user._id);
    var ajaxOpts = Object.assign({
      method: 'PUT',
      body: user,
    }, opts.ajax || {});

    return fetchJSON(db.fetch, path, ajaxOpts);
  });
});

var changeUsername = toCallback(function (oldUsername, newUsername, opts) {
  var db = this;
  var USERNAME_PREFIX = 'org.couchdb.user:';
  var updateUser = function (user, opts) {
    var path = usersPath + '/' + encodeURIComponent(user._id);
    var updateOpts = Object.assign({
      method: 'PUT',
      body: user
    }, opts.ajax);
    return fetchJSON(db.fetch, path, updateOpts);
  };
  if (typeof opts === 'undefined') {
    opts = {};
  }
  opts.ajax = opts.ajax || {};
  if (['http', 'https'].indexOf(db.type()) === -1) {
    return Promise.reject(new AuthError('This plugin only works for the http/https adapter. ' +
      'So you should use new PouchDB("http://mysite.com:5984/mydb") instead.'));
  }
  if (!newUsername) {
    return Promise.reject(new AuthError('You must provide a new username'));
  }
  if (!oldUsername) {
    return Promise.reject(new AuthError('You must provide a username to rename'));
  }

  return db.getUser(newUsername, opts)
  .then(function () {
    var error = new AuthError('user already exists');
    error.taken = true;
    throw error;
  }, function () {
    return db.getUser(oldUsername, opts);
  })
  .then(function (user) {
    var newUser = clone(user);
    delete newUser._rev;
    newUser._id = USERNAME_PREFIX + newUsername;
    newUser.name = newUsername;
    newUser.roles = opts.roles || user.roles || {};
    return updateUser(newUser, opts).then(function () {
      user._deleted = true;
      return updateUser(user, opts);
    });
  });
});

var plugin = {};

plugin.login = logIn;
plugin.logIn = logIn;
plugin.logout = logOut;
plugin.logOut = logOut;
plugin.getSession = getSession;

plugin.getMembership = getMembership;
plugin.signUpAdmin = signUpAdmin;
plugin.deleteAdmin = deleteAdmin;

plugin.signup = signUp;
plugin.signUp = signUp;
plugin.getUser = getUser;
plugin.putUser = putUser;
plugin.deleteUser = deleteUser;
plugin.changePassword = changePassword;
plugin.changeUsername = changeUsername;

if (typeof window !== 'undefined' && window.PouchDB) {
  window.PouchDB.plugin(plugin);
}

export default plugin;
